/**
 * Sélecteur date + heure où l'heure est contrainte au quart d'heure
 * (00 / 15 / 30 / 45), pour la prise de rendez-vous d'équipe (défi,
 * entraînement). Un champ date classique + un champ heure avec un pas de
 * 15 minutes (arrondi à la volée si l'utilisateur saisit une valeur libre).
 */

interface QuarterHourDateTimeInputProps {
  /** Valeur au format "yyyy-MM-ddTHH:mm" (comme un input datetime-local) */
  value: string;
  onChange: (value: string) => void;
  /** Date/heure minimale autorisée (même format) : empêche de choisir un jour antérieur dans le calendrier */
  min?: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function roundToQuarterHour(hh: number, mm: number): { hh: number; mm: number } {
  let rounded = Math.round(mm / 15) * 15;
  let hours = hh;
  if (rounded === 60) { rounded = 0; hours = (hours + 1) % 24; }
  return { hh: hours, mm: rounded };
}

export default function QuarterHourDateTimeInput({ value, onChange, min }: QuarterHourDateTimeInputProps) {
  const [datePart, timePart] = value.split('T');
  const safeDate = datePart || '';
  const safeTime = timePart || '00:00';
  const minDate = min?.split('T')[0];
  // Un input date natif ne contraint que le jour ; si l'utilisateur choisit le jour
  // minimal autorisé, on contraint aussi l'heure côté champ time pour éviter un
  // rendez-vous passé le même jour.
  const minTime = min && minDate === safeDate ? min.split('T')[1] : undefined;

  const setDate = (d: string) => onChange(`${d}T${safeTime}`);
  const setTime = (t: string) => {
    // Tant que l'utilisateur édite un sous-champ (heure ou minute) du input
    // natif, celui-ci renvoie une valeur vide jusqu'à ce que les deux soient
    // renseignés : si on renvoyait alors onChange("...T00:00"), le composant
    // contrôlé écraserait la saisie en cours et bloquerait tout à 00 (le bug
    // rapporté). On ignore donc les valeurs incomplètes.
    const match = /^(\d{2}):(\d{2})$/.exec(t);
    if (!match) return;
    const hh = Number(match[1]);
    const mm = Number(match[2]);
    const { hh: rh, mm: rm } = roundToQuarterHour(hh, mm);
    onChange(`${safeDate}T${pad(rh)}:${pad(rm)}`);
  };

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input type="date" value={safeDate} min={minDate} onChange={(e) => setDate(e.target.value)} style={{ flex: 1 }} />
      <input type="time" step={900} min={minTime} value={safeTime} onChange={(e) => setTime(e.target.value)} style={{ width: 110 }} />
    </div>
  );
}
